import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useDrag } from 'react-dnd'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import saveAs from 'file-saver'
import { ItemType } from '../../item-type'
import { IconChoices } from '../../../../common/Icon'
import { openUrl, parseUrlWhitespaces } from '../../../../../helpers/url'
import { ViewNames, reverseUrl } from '../../../../../navigation/reverseUrl'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { useAuthenticatedUserId } from '../../../../../hooks/useAuthenticatedUserId'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import { isUserAdminInSociety } from '../../../../../helpers/society'
import { DocumentListRowItem } from './DocumentListRowItem'
import { DocumentListCardItem } from './DocumentListCardItem'
import { IDocumentModel } from '../../../../../interfaces/models/documents.interfaces'
import { getChildDocuments } from '../../../../../state/stores/documents'
import { documentQueries } from '../../../../../api/documents'

interface DocumentsListItemDraggableProps {
  document: IDocumentModel
  documentRoute: ViewNames
  openEditFolderModal: () => void
  openEditDocumentModal: () => void
  openRemoveConfirmationModal: () => void
  isListMode: boolean
  isOver?: boolean
}

export const DocumentsListItemDraggable = ({
  document,
  documentRoute,
  openEditFolderModal,
  openEditDocumentModal,
  openRemoveConfirmationModal,
  isListMode,
  isOver,
}: DocumentsListItemDraggableProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { data: societyDocuments } = useQuery(
    documentQueries.getPerSocietyAndFolder(society?._id || '', document._id)
  )
  const userId = useAuthenticatedUserId() as string
  const navigate = useNavigate()

  const getType = (): ItemType => {
    switch (document.type) {
      case 'folder':
        return ItemType.FOLDER
      default:
        return ItemType.DOCUMENT
    }
  }

  const documentType = getType()

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: documentType,
    item: document,
    collect: (monitor) => ({
      item: monitor.getItem(),
      isDragging: monitor.isDragging(),
    }),
  }))

  const downloadDocument = (): void => {
    if (!document || !document.url) return

    axios
      .get(document.url, { responseType: 'blob', withCredentials: false })
      .then((response) => {
        saveAs(new Blob([response.data]), document.name)
      })
  }

  const childDocuments = getChildDocuments(societyDocuments || [], document._id)

  const isAdmin = isUserAdminInSociety(society, userId)

  const icon =
    documentType === ItemType.FOLDER ? IconChoices.FOLDER : IconChoices.DOCUMENT

  const isFolder = documentType === ItemType.FOLDER

  const openFolder = (): void => {
    if (isFolder) {
      navigate(
        reverseUrl(documentRoute, {
          id: society?._id,
          documentId: document._id,
        })
      )
    }
  }

  const openFile = (): void => {
    if (!isFolder && document.url) {
      openUrl(parseUrlWhitespaces(document.url))
    }
  }

  const openItem = (): void => {
    if (isFolder) {
      openFolder()
    }
    if (!isFolder) {
      openFile()
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const createDropdownOptions = () => {
    const options = []

    if (!isFolder) {
      options.push({
        value: 'download',
        label: translate('common.actions.download'),
        onClick: async () => downloadDocument(),
      })
    }

    options.push({
      value: 'edit',
      label: translate('common.actions.edit'),
      onClick: () =>
        isFolder ? openEditFolderModal() : openEditDocumentModal(),
    })
    options.push({
      value: 'remove',
      label: translate('common.actions.delete'),
      onClick: async () => openRemoveConfirmationModal(),
      destructive: true,
    })

    return options
  }
  const dropdownOptions = createDropdownOptions()

  const getDocumentSubTitleText = (): string => {
    const numFolders = childDocuments.filter(
      (_document) => _document.type === 'folder'
    ).length
    const numFiles = childDocuments.length - numFolders

    const items = []
    if (numFolders > 0) {
      items.push(
        `${numFolders} ${
          numFolders === 1
            ? translate('documentsList.document.folderSingular')
            : translate('documentsList.document.folderPlural')
        }`
      )
    }
    if (numFiles > 0) {
      items.push(
        `${numFiles} ${
          numFolders === 1
            ? translate('documentsList.document.documentSingular')
            : translate('documentsList.document.documentPlural')
        }`
      )
    }

    return items.join(', ')
  }

  const documentSubtitle = getDocumentSubTitleText()

  return (
    <>
      {isListMode ? (
        <DocumentListRowItem
          isAdmin={isAdmin}
          openItem={openItem}
          documentTitle={document.name || ''}
          documentSubtitle={documentSubtitle}
          documentIcon={icon}
          dropdownOptions={dropdownOptions}
          isDragging={isDragging}
          dragPreview={dragPreview}
          drag={drag}
          isFolder={isFolder}
          isOver={isOver}
          thumbnail={document.thumbnail}
        />
      ) : (
        <DocumentListCardItem
          isAdmin={isAdmin}
          openItem={openItem}
          documentTitle={document.name || ''}
          documentSubtitle={documentSubtitle}
          documentIcon={icon}
          dropdownOptions={dropdownOptions}
          isDragging={isDragging}
          dragPreview={dragPreview}
          drag={drag}
          isOver={isOver}
          thumbnail={document.thumbnail}
        />
      )}
    </>
  )
}
